import React, { useState, useEffect } from "react";
import { DeleteFilled, ExclamationCircleFilled } from "@ant-design/icons";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  TimePicker,
  message,
  DatePicker,
} from "antd";
import {
  useGetCarsQuery,
  useGetDriversQuery,
  useGetBranchesQuery,
  useGetCountriesQuery,
  useAddItineraryMutation,
  useGetItinerariesQuery,
  useDeleteItineraryMutation,
} from "../../../redux/Service/api";
import Heading from "../../../Components/Heading/Heading";
import ServerError from "../../../Components/utils/ServerError";
import dayjs from "dayjs";

interface Itinerary {
  CarName: string;
  DriverName: string;
  BranchName: string;
  CountryFromName: string;
  CountryToName: string;
  ItineraryID: number;
  StartTime: string;
  ArriveTime: string;
  SitValue: number;
}

const Itineraries: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [form] = Form.useForm();

  const { data: carsData, isLoading: isCarsLoading } =
    useGetCarsQuery(undefined);
  const { data: driversData, isLoading: isDriversLoading } =
    useGetDriversQuery(undefined);
  const { data: branchesData, isLoading: isBranchesLoading } =
    useGetBranchesQuery(undefined);
  const { data: countriesData, isLoading: isCountriesLoading } =
    useGetCountriesQuery(undefined);

  const {
    data: ItinerariesData,
    isLoading: isItinerariesLoading,
    isError,
  } = useGetItinerariesQuery(undefined);

  const [addItinerary, { isLoading: isAdding, isError: isAddingError }] =
    useAddItineraryMutation();

  const [deleteItinerary, { isLoading: delIsLoading }] =
    useDeleteItineraryMutation();

  const itineraries: Itinerary[] = ItinerariesData?.ResponseObject ?? [];
  const cars = carsData?.ResponseObject ?? [];
  const drivers = driversData?.ResponseObject ?? [];
  const branches = branchesData?.ResponseObject ?? [];
  const countries = countriesData?.ResponseObject ?? [];

  const handleAddItinerary = async () => {
    try {
      const values = await form.validateFields();
      const itineraryPayload = {
        CarId: values.CarId,
        DriverID: values.DriverID,
        BranchID: values.BranchID,
        CountryFromID: values.CountryFromID,
        CountryToID: values.CountryToID,
        StartTime: values.StartTime.format("HH:mm:ss"),
        ArriveTime: values.ArriveTime.format("HH:mm:ss"),
        SitValue: values.SitValue,
      };

      // console.log("itineraryPayload", itineraryPayload);
      // return;

      const response = await addItinerary(itineraryPayload);
      if (response?.data) {
        message.success("تمت إضافة خط السير بنجاح");
        form.resetFields();
        setOpenModal(false);
      }
    } catch (error) {
      console.error("خطأ أثناء إضافة خط السير:", error);
      message.error("فشل في إضافة خط السير. يرجى المحاولة مرة أخرى.");
    }
  };
  const handleDeleteItinerary = (id: number) => {
    Modal.confirm({
      title: "هل أنت متأكد أنك تريد حذف هذا الخط",
      icon: <ExclamationCircleFilled />,
      content: "بالضغط على موافق سيتم حذف الخط بشكل دائم.",
      async onOk() {
        try {
          await deleteItinerary(id);
          message.success("تم حذف الخط بنجاح");
        } catch (error) {
          console.error(error);
          message.error("فشل في حذف الخط.");
        }
      },
    });
  };
  const columns = [
    {
      title: "اسم السيارة",
      dataIndex: "CarId",
      key: "CarId",
      render: (value: any) => {
        console.log("cars", cars);
        return (
          cars.find((car: any) => car.CarID === value)?.Marka +
            " - " +
            cars.find((car: any) => car.CarID === value)?.Modal || "Unknown"
        );
      },
    },
    {
      title: "اسم السائق",
      dataIndex: "DriverName",
      key: "DriverName",
    },
    {
      title: "اسم الفرع",
      dataIndex: "BranchName",
      key: "BranchName",
    },
    {
      title: "الدولة (من)",
      dataIndex: "CountryFromName",
      key: "CountryFromName",
    },
    {
      title: "الدولة (إلى)",
      dataIndex: "CountryToName",
      key: "CountryToName",
    },
    {
      title: "وقت البداية",
      dataIndex: "StartTime",
      key: "StartTime",
    },
    {
      title: "وقت الوصول",
      dataIndex: "ArriveTime",
      key: "ArriveTime",
    },
    {
      title: "سعر الكرسي",
      dataIndex: "SitValue",
      key: "SitValue",
    },
    {
      title: "الإجراءات",
      key: "actions",
      render: (_: any, iternerary: any) => (
        <div className="flex gap-2">
          <Button
            type="text"
            icon={<DeleteFilled />}
            danger
            onClick={() => handleDeleteItinerary(iternerary.ItineraryID)}
          />
        </div>
      ),
    },
  ];

  return (
    <section className="p-4 section_padding mt-0 pt-0 relative">
      <Heading heading="خطوط السير" />

      {isAddingError ? <ServerError /> : null}

      <div className="text-center my-3">
        <Button
          className="w-[150px]"
          type="primary"
          onClick={() => setOpenModal(true)}
        >
          إضافة خط سير
        </Button>
      </div>

      <div className="mx-auto">
        <Table
          columns={columns}
          dataSource={itineraries}
          loading={isItinerariesLoading}
          rowKey="ItineraryID"
          pagination={{ pageSize: 5 }}
        />
      </div>

      <Modal
        title="إضافة خط سير"
        centered
        open={openModal}
        onCancel={() => {
          setOpenModal(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          name="add_itinerary_form"
          onFinish={handleAddItinerary}
        >
          <Form.Item
            label="السيارة"
            name="CarId"
            rules={[{ required: true, message: "يرجى اختيار السيارة" }]}
          >
            <Select size="large" placeholder="اختر السيارة">
              {cars.map((car: any) => (
                <Select.Option key={car.CarID} value={car.CarID}>
                  {car.Marka} / {car.Modal} / {car.CarNumber}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="السائق"
            name="DriverID"
            rules={[{ required: true, message: "يرجى اختيار السائق" }]}
          >
            <Select size="large" placeholder="اختر السائق">
              {drivers.map((driver: any) => (
                <Select.Option key={driver.DriverID} value={driver.DriverID}>
                  {driver.DriverName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="الفرع"
            name="BranchID"
            rules={[{ required: true, message: "يرجى اختيار الفرع" }]}
          >
            <Select size="large" placeholder="اختر الفرع">
              {branches.map((branch: any) => (
                <Select.Option key={branch.BranchID} value={branch.BranchID}>
                  {branch.BranchName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="الدولة (من)"
            name="CountryFromID"
            rules={[{ required: true, message: "يرجى اختيار الدولة (من)" }]}
          >
            <Select size="large" placeholder="اختر الدولة (من)">
              {countries.map((country: any) => (
                <Select.Option
                  key={country.CountryID}
                  value={country.CountryID}
                >
                  {country.CountryName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="الدولة (إلى)"
            name="CountryToID"
            rules={[{ required: true, message: "يرجى اختيار الدولة (إلى)" }]}
          >
            <Select size="large" placeholder="اختر الدولة (إلى)">
              {countries.map((country: any) => (
                <Select.Option
                  key={country.CountryID}
                  value={country.CountryID}
                >
                  {country.CountryName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="وقت البداية"
            name="StartTime"
            rules={[{ required: true, message: "يرجى اختيار وقت البداية" }]}
          >
            <TimePicker
              size="large"
              className="w-full"
              placeholder="اختر وقت البداية"
            />
          </Form.Item>

          <Form.Item
            label="وقت الوصول"
            name="ArriveTime"
            rules={[{ required: true, message: "يرجى اختيار وقت الوصول" }]}
          >
            <TimePicker
              size="large"
              className="w-full"
              placeholder="اختر وقت الوصول"
            />
          </Form.Item>

          <Form.Item
            label="سعر الكرسي"
            name="SitValue"
            rules={[{ required: true, message: "يرجى إدخال سعر الكرسي" }]}
          >
            <Input size="large" type="number" placeholder="أدخل سعر الكرسي" />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isAdding}
              className="w-full"
            >
              إضافة
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </section>
  );
};

export default Itineraries;
